import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Shield, ExternalLink, RefreshCw, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProvablyFair() {
  const [testResults, setTestResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runTest = async (gameType) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/games/demo/${gameType}`);
      setTestResults({
        game: gameType,
        result: response.data,
        timestamp: new Date().toISOString()
      });
      toast.success(`${gameType} test completed`);
    } catch (error) {
      toast.error('Test failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Link to="/app/coinflip" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors">
          ← Back to Games
        </Link>

        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-6">
            <Shield className="text-green-400" size={40} />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
            Provably Fair
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            All game outcomes on Rbx.Casino are generated using Random.org's true random number generator
          </p>
        </div>

        {/* Random.org Section */}
        <div className="glass rounded-2xl p-8 mb-8">
          <div className="flex items-center gap-4 mb-6">
            <img 
              src="https://www.random.org/images/logo.png" 
              alt="Random.org" 
              className="h-10 bg-white rounded px-2 py-1"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <h2 className="font-display text-2xl font-bold text-white">Powered by Random.org</h2>
              <a 
                href="https://www.random.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
              >
                www.random.org <ExternalLink size={14} />
              </a>
            </div>
          </div>

          <p className="text-slate-300 mb-6">
            Random.org offers true random numbers generated from atmospheric noise, which is more unpredictable 
            than the pseudo-random number algorithms typically used in computer programs. This ensures that 
            every game outcome is genuinely random and cannot be predicted or manipulated.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
              <h3 className="font-bold text-white mb-2">True Randomness</h3>
              <p className="text-slate-400 text-sm">
                Numbers are generated from atmospheric noise, not algorithms
              </p>
            </div>
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
              <h3 className="font-bold text-white mb-2">Third-Party Verified</h3>
              <p className="text-slate-400 text-sm">
                Random.org is independently audited and certified
              </p>
            </div>
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
              <h3 className="font-bold text-white mb-2">API Integration</h3>
              <p className="text-slate-400 text-sm">
                We use Random.org's JSON-RPC API for all game outcomes
              </p>
            </div>
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
              <h3 className="font-bold text-white mb-2">Transparent</h3>
              <p className="text-slate-400 text-sm">
                All random numbers can be verified against Random.org
              </p>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="glass rounded-2xl p-8 mb-8">
          <h2 className="font-display text-2xl font-bold text-white mb-6">How It Works</h2>
          
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 font-bold">1</div>
              <div>
                <h3 className="font-bold text-white mb-1">You Place a Bet</h3>
                <p className="text-slate-400 text-sm">Choose your game, amount, and make your selection</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 font-bold">2</div>
              <div>
                <h3 className="font-bold text-white mb-1">Random.org Generates the Outcome</h3>
                <p className="text-slate-400 text-sm">Our server requests true random numbers from Random.org's API</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 font-bold">3</div>
              <div>
                <h3 className="font-bold text-white mb-1">Result is Determined</h3>
                <p className="text-slate-400 text-sm">The random number determines the game outcome (coinflip, crash point, roulette number, etc.)</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 font-bold">✓</div>
              <div>
                <h3 className="font-bold text-white mb-1">Fair Result</h3>
                <p className="text-slate-400 text-sm">You receive the outcome based on true randomness, not manipulated algorithms</p>
              </div>
            </div>
          </div>
        </div>

        {/* Game-Specific Info */}
        <div className="glass rounded-2xl p-8 mb-8">
          <h2 className="font-display text-2xl font-bold text-white mb-6">Game Random Number Usage</h2>
          
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
              <h3 className="font-bold text-yellow-400 mb-2">Coinflip</h3>
              <p className="text-slate-400 text-sm">Random integer 0-1 from Random.org. 0 = Tails, 1 = Heads</p>
            </div>
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
              <h3 className="font-bold text-green-400 mb-2">Crash</h3>
              <p className="text-slate-400 text-sm">Random integer 100-10000 determines the crash multiplier point</p>
            </div>
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
              <h3 className="font-bold text-red-400 mb-2">Roulette</h3>
              <p className="text-slate-400 text-sm">Random integer 0-36 from Random.org determines the winning number</p>
            </div>
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
              <h3 className="font-bold text-purple-400 mb-2">Blackjack</h3>
              <p className="text-slate-400 text-sm">Random card selection for each draw from the deck</p>
            </div>
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
              <h3 className="font-bold text-cyan-400 mb-2">Cases</h3>
              <p className="text-slate-400 text-sm">Random integer 1-10000 determines item rarity outcome</p>
            </div>
          </div>
        </div>

        {/* Live Test */}
        <div className="glass rounded-2xl p-8">
          <h2 className="font-display text-2xl font-bold text-white mb-6">Live Verification Test</h2>
          <p className="text-slate-400 mb-6">
            Test our Random.org integration live. Each click generates a real random number from Random.org.
          </p>

          <div className="flex flex-wrap gap-3 mb-6">
            <Button 
              onClick={() => runTest('coinflip')} 
              disabled={loading}
              className="bg-yellow-600 hover:bg-yellow-500"
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />}
              Test Coinflip
            </Button>
            <Button 
              onClick={() => runTest('crash')} 
              disabled={loading}
              className="bg-green-600 hover:bg-green-500"
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />}
              Test Crash
            </Button>
            <Button 
              onClick={() => runTest('roulette')} 
              disabled={loading}
              className="bg-red-600 hover:bg-red-500"
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />}
              Test Roulette
            </Button>
          </div>

          {testResults && (
            <div className="p-4 rounded-xl bg-slate-800 border border-green-500/30">
              <h3 className="font-bold text-green-400 mb-2">Test Result</h3>
              <pre className="text-slate-300 text-sm overflow-x-auto">
{JSON.stringify(testResults, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Footer Link */}
        <div className="text-center mt-8">
          <a 
            href="https://api.random.org/json-rpc/2/basic" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 flex items-center justify-center gap-2"
          >
            View Random.org API Documentation <ExternalLink size={16} />
          </a>
        </div>
      </div>
    </div>
  );
}
